<template>
  <div class="container-fluid">
    <page-title
      :isDataLoading="isLoading"
      :title="this.$t('battery.gradients')"
      :iconClasses="
        formControl.data_value === 'level_points'
          ? 'fas fa-battery-half'
          : 'fas fa-thermometer-half'
      "
      :canDownload="false"
      :isTimelineDisplayed="true"
    />

    <div class="container-fluid col-lg-11 battery">
      <div class="row justify-content-md-center mb-4">
        <form-controls
          :controls="controlsConfig"
          v-on:updateControl="handleChange"
          v-on:setDefaultValues="setFormControlsDefault"
        />
      </div>
      <div class="row mt-2">
        <div class="container">
          <ul class="nav nav-tabs justify-content-center" id="myTab" role="tablist">
            <li class="nav-item">
              <a
                class="nav-link active"
                id="gradient-tab"
                data-toggle="tab"
                href="#gradient"
                role="tab"
                aria-controls="time"
                aria-selected="true"
                >{{ $t('chart.time') }}</a
              >
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                id="distribution-tab"
                data-toggle="tab"
                href="#distribution"
                role="tab"
                aria-controls="distribution"
                aria-selected="false"
                >{{ $t('chart.data_type.distribution') }}</a
              >
            </li>
          </ul>
          <div class="tab-content mb-3" id="myTabContent">
            <div
              class="tab-pane fade show active pb-4"
              id="gradient"
              role="tabpanel"
              aria-labelledby="gradient-tab"
            >
              <div v-if="gradientsDownloaded && dataset && dataset.length > 0">
                <stacked-gradient-chart
                  :chartId="chartId"
                  :dataset="dataset"
                  :batteryForm="formControl"
                  :site="formControl.site"
                >
                </stacked-gradient-chart>
              </div>
            </div>
            <div
              class="tab-pane fade pb-4"
              id="distribution"
              role="tabpanel"
              aria-labelledby="distribution-tab"
            >
              <div v-if="distributionDownloaded">
                <gradient-distribution-chart
                  :config="datasetDistribution"
                  :chartId="'gradientDistribution'"
                  v-if="datasetDistribution.items"
                ></gradient-distribution-chart>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import datePicker from 'vue-bootstrap-datetimepicker'
import { utilsMixin, sitesMixin } from '../utils/mixins'
import moment from 'moment'

export default {
  name: 'BatteryGradient',
  dependencies: ['technicalService'],
  mixins: [utilsMixin, sitesMixin],
  props: ['period'],
  data() {
    return {
      datePicker: datePicker,
      config: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$locale,
      },
      timelineId: 'timeline',
      dataset: {},
      datasetDistribution: {},
      gradientsUrl: '',
      chartId: 'stackedGradientChart',
      stackedChartLoading: false,
      gradientsDownloaded: false,
      distributionChartLoading: false,
      distributionDownloaded: false,
      formControl: {},
    }
  },
  watch: {
    period: {
      handler() {
        this.fetchBatteryGradients()
        this.fetchBatteryGradientsDistribution()
      },
      immediate: true,
    },
  },
  mounted() {},
  components: {
    'stacked-gradient-chart': () => import('./charts/exploitation/StackedGradientChart'),
    'gradient-distribution-chart': () => import('./charts/GradientDistributionChart'),
    'page-title': () => import('./utils/PageTitle'),
    'form-controls': () => import('./utils/FormControls'),
  },
  computed: {
    siteIDs() {
      if (!this.formControl.site) {
        return this.sites
      }
      return [this.formControl.site]
    },
    allowedDataTypes() {
      if (this.formControl.data_value == 'level_points') {
        return [{ value: 'gradient', label: 'Gradient moyen' }]
      } else {
        return [
          { value: 'gradient', label: 'Gradient moyen' },
          { value: 'max', label: 'Max' },
          { value: 'min', label: 'Min' },
          { value: 'average', label: 'Moyenne' },
        ]
      }
    },
    isLoading() {
      return this.stackedChartLoading || this.distributionChartLoading
    },
    allDownloaded() {
      return this.gradientsDownloaded && this.distributionDownloaded
    },
    controlsConfig() {
      let config = [
        {
          label: this.$t('filter.by_site'),
          type: 'select',
          vModel: 'site',
          default: this.$route.params.site ? this.$route.params.site : 'france.chambord.chateau',
          options: [
            {
              value: 'all',
              label: this.$t('select_all'),
            },
            ...this.getCurrentUser.sites.map(site => ({
              value: site.site,
              label: site.name,
            })),
          ],
        },
        {
          label: this.$t('battery.infos'),
          type: 'select',
          vModel: 'data_value',
          default: 'level_points',
          options: [
            { value: 'level_points', label: this.$t('battery.levels') },
            { value: 'temperature_points', label: this.$t('battery.temperatures') },
          ],
        },
        {
          label: 'Type',
          type: 'select',
          vModel: 'data_type',
          default: 'gradient',
          options: this.allowedDataTypes,
        },
      ]

      return config
    },
  },
  methods: {
    handleChange(change) {
      this.formControl[change.vModel] = change.value

      this.fetchBatteryGradients()
      this.fetchBatteryGradientsDistribution()
      window.history.pushState(
        null,
        null,
        '/battery/gradients/site/' +
          this.formControl.site +
          '/start/' +
          this.start +
          '/end/' +
          this.end,
      )
    },

    fetchBatteryGradients() {
      this.start = moment(this.period[0], this.dateFormat).format('YYYY-MM-DD')
      this.end = moment(this.period[1], this.dateFormat).format('YYYY-MM-DD')

      this.stackedChartLoading = true

      if (!this.formControl.site) {
        return
      }

      this.technicalService
        .getBatteryGradientsForSiteAndPeriod(
          this.formControl.site,
          this.formControl.data_value,
          this.formControl.data_type,
          this.start,
          this.end,
        )
        .then(data => {
          this.dataset = data.result
          this.gradientsDownloaded = true
          this.stackedChartLoading = false
        })
        .catch(error => {
          console.error('Error when fetchBatteryGradients:', error)
          this.stackedChartLoading = false
          this.gradientsDownloaded = false
        })
    },
    fetchBatteryGradientsDistribution() {
      window.history.pushState(
        null,
        null,
        '/battery/gradients/site/' +
          this.formControl.site +
          '/start/' +
          this.start +
          '/end/' +
          this.end,
      )

      this.distributionChartLoading = true

      this.technicalService
        .getBatteryGradientsDistributionForSiteAndPeriod(
          this.formControl.site,
          this.formControl.data_value,
          this.formControl.data_type,
          this.start,
          this.end,
        )
        .then(data => {
          this.datasetDistribution = data.result
          this.datasetDistribution['answer_type'] = 'Slider'
          this.distributionChartLoading = false
          this.distributionDownloaded = true
        })
        .catch(error => {
          console.error('Error', error)
          this.distributionChartLoading = false
          this.distributionDownloaded = false
        })
    },
    onDateRangeUpdate(dateRange) {
      this.startDate = moment(this.startDate, 'YYYY-MM-DD').format(this.dateFormat)
      this.endDate = moment(this.endDate, 'YYYY-MM-DD').format(this.dateFormat)

      if (this.startDate == dateRange.start && this.endDate == dateRange.end) {
        return
      }

      clearTimeout(this.timeout)

      dateRange.start = moment(dateRange.start, this.dateFormat).isValid()
        ? dateRange.start
        : moment()
            .subtract(1, 'month')
            .format(this.dateFormat)
      dateRange.end = moment(dateRange.end, this.dateFormat).isValid()
        ? dateRange.end
        : moment().format(this.dateFormat)

      this.datePickerStart = dateRange.start
      this.datePickerEnd = dateRange.end

      this.startDate = dateRange.start
      this.endDate = dateRange.end

      this.timeout = setTimeout(() => {
        this.start = moment(this.startDate, this.dateFormat).format('YYYY-MM-DD')
        this.end = moment(this.endDate, this.dateFormat).format('YYYY-MM-DD')

        this.fetchBatteryGradients()
        this.fetchBatteryGradientsDistribution()
      }, 500)
    },
  },
}
</script>

<style>
.starter-template {
  padding: 3rem 1.5rem;
  text-align: center;
}

.table > tbody > tr > td {
  vertical-align: middle;
}

.table > tbody > tr > th {
  vertical-align: middle;
}
</style>
